import React, { FC, PropsWithChildren, useRef } from 'react'
import _ from 'lodash'
import { getHostingAppId, isFeedieEmail } from '@feedyou/utils'
import { css } from 'styled-components'

import CollapsibleForm from '../../../CollapsibleForm'
import { SettingsPopupProps } from '../../SettingsPopup'
import { generalSettingsSchema, uiSchema } from './generalSettingsSchema'
import { useBotConfiguration } from '../../../../features/serverBots'
import { useSettingsPopupState } from '../../hooks'
import { ModalContent, ModalFooter, ModalFooterButton } from '../../styles'
import { endpointHelpers } from '../../../../features/serverBots/endpointHelpers'
import { useBotId } from '../../../../store/flow'
import { useUserInfo } from '../../../../store/user'
import { SdkVersionWidget } from '../../../design-system/FeedbotRjsfForm/components/widgets'
import { getPatchFromFormData } from './getPatchFromFormData'
import { Spacer } from '../../../design-system/Spacer'
import { ArchiveBotButton, DeleteBotButton } from '../../Buttons'
import { useSdkBuildInfo } from '../../../../features/hostingVersions/hooks/useSdkBuildInfo'
import {
  getSelectedVersionFromDataTest,
  isSdkVersionFormEditedFromDataTest
} from '../../../design-system/FeedbotRjsfForm/components/widgets/ButtonWidget/SdkVersionWidget/components/ChangeSdkVersionForm'
import { alert } from '../../../../lib/bootbox'

export const BotConfigurationForm: FC<SettingsPopupProps> = props => {
  const botId = useBotId()
  const config = useBotConfiguration()

  const appKind = useSdkBuildInfo()?.appKind
  const email = useUserInfo()?.email
  const hostingName = getHostingAppId(config?.apiUrl) || 'Not launched'
  const schema = generalSettingsSchema

  const [triggerRequest, requestState] = endpointHelpers.updateBotConfiguration.useMutation()

  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const { formData, onFormDataChange } = useSettingsPopupState(() =>
    _.pick(config, Object.keys(schema.properties))
  )

  if (!config) {
    return null
  }

  return (
    <>
      <ModalContent>
        <ActionsRow label="Hosting version">
          <SdkVersionWidget />
        </ActionsRow>

        {appKind && (
          <ActionsRow label="Hosting kind">
            {appKind === 'functionapp' ? 'Function app' : 'Web app'}
          </ActionsRow>
        )}

        {isFeedieEmail(email) && <ActionsRow label="Hosting name"> {hostingName}</ActionsRow>}

        {/*<ActionsRow label="Directline secret">{directLineSecret}</ActionsRow>*/}

        <CollapsibleForm
          schema={schema}
          uiSchema={uiSchema}
          formData={formData}
          className={props.className}
          liveValidate={true}
          onChange={onFormDataChange}
          submitButtonRef={submitButtonRef}
          onSubmit={async ({ formData }) => {
            triggerRequest({ id: botId, configurationPatch: getPatchFromFormData(formData) })
          }}
        />

        <ActionsRow label="Actions">
          <DeleteBotButton />
          <Spacer width="10px" display="inline-block" />
          <ArchiveBotButton />
        </ActionsRow>
      </ModalContent>

      <ModalFooter>
        <ModalFooterButton
          label="Close"
          className="e2e-close-bot-modal-button"
          onClick={props.close}
        />

        <ModalFooterButton
          emphasis="primary"
          disabled={requestState.isLoading}
          loading={requestState.isLoading}
          label="Apply now"
          className="e2e-modal-success-button"
          onClick={() => {
            submitButtonRef.current?.click?.()

            if (isSdkVersionFormEditedFromDataTest()) {
              console.log(
                document
                  .querySelector('[data-selected-version]')
                  ?.getAttribute('data-selected-version')
              )

              alert({
                title: '<b>Unconfirmed Hosting version Change</b>',
                message: `You have selected a new hosting version <b>${getSelectedVersionFromDataTest()}</b>, but you haven't applied it. If you wish to change the hosting version, you need to click the "Set" button next to the version select.`
              })
            }
          }}
        />
      </ModalFooter>
    </>
  )
}

const ActionsRow: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => {
  return (
    <div css={actionsRowCss.outerWrapper}>
      <span css={actionsRowCss.label}>{label}</span>

      <span css={actionsRowCss.content}>{children}</span>
    </div>
  )
}

const actionsRowCss = {
  outerWrapper: css`
    display: flex;
    flex-direction: row;
    min-height: 34px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  `,
  label: css`
    font-weight: 700;
    margin-right: 10px;
    display: inline-block;
  `,
  content: css`
    width: calc(80% - 26px);
  `
}
